<template>
  <div v-if="!$apollo.queries.getBusinessEntityDetails.loading">
    <FlowHeading
      :title="$t('Add your business details')"
      :sub-title="
        $t('This information allows us to show your correct business information on your bills.')
      "
    />

    <div class="tw-mb-5">
      <WdsInput
        v-model="businessName"
        name="businessName"
        autocomplete="organization"
        placeholder="Ex. Worksome Aps."
        :label="$t('Business entity name')"
        lg
        required
        :error="errorBusinessName"
        @focusout="v$.businessName.$touch"
      />
    </div>

    <div class="tw-mb-5">
      <WdsInput
        v-model="businessNumber"
        name="businessNumber"
        :label="$t('SIREN / SIRET')"
        placeholder="Ex. 265849012"
        :error="errorBusinessNumber"
        required
        lg
        @focusout="v$.businessNumber.$touch"
      />
    </div>

    <div class="tw-mb-5">
      <WdsInput
        v-model="cityOfRegistration"
        name="cityOfRegistration"
        autocomplete="organization"
        placeholder="Ex. Paris"
        :label="$t('City of registration')"
        lg
        required
        :error="errorCityOfRegistration"
        @focusout="v$.cityOfRegistration.$touch"
      />
    </div>

    <div class="sales-tax">
      <WdsRadioGroup
        v-model:checked="hasSalesTax"
        name="sales-tax"
        :label="$t('Are you registered for VAT?')"
      >
        <WdsRadio :value="false">
          <WdsText :text="$t('No, I will be billing without VAT')" />
        </WdsRadio>
        <WdsRadio :value="true">
          <WdsText :text="$t('Yes, I have a VAT registration')" />
        </WdsRadio>
      </WdsRadioGroup>
      <div class="sales-tax-number">
        <WdsInput
          v-if="hasSalesTax"
          v-model="salesTaxNumber"
          name="salesTaxNumber"
          :placeholder="$t('VAT number')"
          :error="errorVatNo"
          lg
          @focusout="v$.salesTaxNumber.$touch"
        />
      </div>
    </div>

    <div class="wds-input-group tw-mt-6 tw-mb-4">
      <WdsButton full-width lg primary :disabled="!canSubmit" @click="callNextScreen">
        <div class="tw-flex">
          <transition name="fade">
            <ButtonSpinner v-if="isSubmitting" size="sm" color="blue" />
          </transition>
          {{ $t('Continue') }}
        </div>
      </WdsButton>
    </div>
    <div v-if="lacksRequiredFields" class="tw-mt-4">
      <WdsStack justify-center>
        <WdsButtonGhost secondary @click="skip">{{ $t('Skip and update later') }}</WdsButtonGhost>
      </WdsStack>
    </div>
  </div>
</template>

<script>
import { ref } from 'vue'
import useVuelidate from '@vuelidate/core'
import {
  WdsButton,
  WdsButtonGhost,
  WdsInput,
  WdsRadio,
  WdsRadioGroup,
  WdsStack,
  WdsText,
} from '@worksome/wds-components'
import gql from 'graphql-tag'
import { helpers, required, requiredIf } from '@vuelidate/validators'
import FlowHeading from '@/components/flows/FlowHeading.vue'
import { useValidatorWithGraphQlError } from '@/composables/errors'
import { FormValidation } from '@/utils/formValidation'
import ButtonSpinner from '@/components/ButtonSpinner.vue'

export default {
  name: 'FrBusinessEntityTypeDetails',

  components: {
    ButtonSpinner,
    FlowHeading,
    WdsButton,
    WdsButtonGhost,
    WdsInput,
    WdsRadio,
    WdsRadioGroup,
    WdsStack,
    WdsText,
  },

  inject: ['skip', 'next'],

  props: {
    entityType: {
      type: String,
      require: true,
      default: 'fr_limited',
    },
  },

  setup() {
    const v$ = useVuelidate()
    const errors = ref({})

    const errorBusinessName = useValidatorWithGraphQlError(
      v$,
      errors,
      'businessName',
      'registered_company_name',
    )
    const errorBusinessNumber = useValidatorWithGraphQlError(
      v$,
      errors,
      'businessNumber',
      'company_no',
    )
    const errorCityOfRegistration = useValidatorWithGraphQlError(
      v$,
      errors,
      'cityOfRegistration',
      'cityOfRegistration',
    )
    const errorVatNo = useValidatorWithGraphQlError(v$, errors, 'salesTaxNumber', 'vat_no')

    return {
      v$,
      errors,
      errorBusinessName,
      errorBusinessNumber,
      errorCityOfRegistration,
      errorVatNo,
    }
  },

  data() {
    return {
      hasSalesTax: undefined,
      businessName: '',
      businessDetailsFinished: false,
      globalId: '',
      salesTaxNumber: '',
      businessNumber: '',
      cityOfRegistration: '',
      isSubmitting: false,
    }
  },
  computed: {
    lacksRequiredFields() {
      return this.v$.$invalid
    },
    canSubmit() {
      return !this.isSubmitting
    },
  },

  methods: {
    async callNextScreen() {
      this.errors = {}
      this.v$.$touch()
      if (this.v$.$invalid) {
        return
      }
      this.isSubmitting = true
      await this.updateBusinessEntityDetails()
      if (Object.keys(this.errors).length === 0) {
        this.next()
      } else {
        this.isSubmitting = false
      }
    },
    async updateBusinessEntityDetails() {
      await this.$apollo
        .mutate({
          mutation: gql`
            mutation updatePrimaryBusinessEntity($input: UpdatePrimaryBusinessEntityInput!) {
              updatePrimaryBusinessEntity(input: $input) {
                globalId
                entityType
                isPrimary
                label
                companyNo
                companyName
                taxNo
                data
              }
            }
          `,
          variables: {
            input: {
              globalId: this.globalId, // must be accountID of current logged in user
              registered_company_name: this.businessName,
              business_entity: this.entityType,
              business_entity_data: JSON.stringify({
                city_of_registration: this.cityOfRegistration,
              }),
              vat_no: this.hasSalesTax ? this.salesTaxNumber : '',
              company_no: this.businessNumber,
            },
          },
          context: {
            headers: {
              'X-FLOW-STEP-COMPLETED': this.$route.fullPath,
            },
          },
        })
        .catch(({ graphQLErrors }) => {
          if (FormValidation.containsFormValidations(graphQLErrors)) {
            this.errors = FormValidation.fromGraphqlErrors(graphQLErrors) ?? {}
          }
        })
    },
  },

  apollo: {
    getBusinessEntityDetails: {
      query: gql`
        query me {
          me {
            activeAccount {
              globalId
              ... on Freelancer {
                primaryBusinessEntity {
                  entityType
                  isPrimary
                  label
                  companyNo
                  companyName
                  taxNo
                  data
                }
              }
            }
          }
        }
      `,
      result({ data }) {
        const info = data.me.activeAccount.primaryBusinessEntity
        this.globalId = data.me.activeAccount.globalId
        if (info) {
          this.businessName = info.companyName ? info.companyName : ''
          this.businessDetailsFinished = true
          const businessData = JSON.parse(info.data)
          this.salesTaxNumber = info.taxNo ?? this.salesTaxNumber
          this.hasSalesTax = !!this.salesTaxNumber
          this.businessNumber = info.companyNo ?? this.businessNumber
          this.cityOfRegistration = businessData?.city_of_registration ?? ''
        }
      },
      update: (data) => data.me,
    },
  },

  validations() {
    return {
      businessName: {
        required: helpers.withMessage($t('Please enter your business name'), required),
      },
      salesTaxNumber: {
        requiredIf: helpers.withMessage(
          $t('Please enter your VAT number'),
          requiredIf(this.hasSalesTax),
        ),
      },
      businessNumber: {
        required: helpers.withMessage(
          $t('Please enter your company registration number'),
          required,
        ),
      },
      cityOfRegistration: {
        required: helpers.withMessage($t('Please enter city of registration'), required),
      },
    }
  },
}
</script>

<style scoped>
.sales-tax {
  margin-top: 20px;
  margin-bottom: 20px;
}
.sales-tax-number {
  margin-left: 25px;
}
:deep(.wds-radio) {
  align-items: flex-start;
}
</style>
